"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";

// After a tab sits in the background for a while, browsers throttle timers and the
// App Router's client cache / prefetched payloads go stale — which can make the page
// feel "frozen" (links do nothing) until a manual reload. Returning focus after a long
// idle, or a bfcache restore, triggers a soft router refresh to revalidate that state.
const STALE_AFTER_MS = 5 * 60 * 1000; // 5 minutes

export default function StaleTabRecovery() {
  const router = useRouter();

  useEffect(() => {
    let hiddenAt: number | null = null;

    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt === null) return;
      const elapsed = Date.now() - hiddenAt;
      hiddenAt = null;
      if (elapsed >= STALE_AFTER_MS && navigator.onLine) {
        router.refresh();
      }
    };

    // Fired when the page is restored from the back/forward (bfcache) — its JS state
    // is resumed wholesale and is almost always stale.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted && navigator.onLine) router.refresh();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [router]);

  return null;
}
